/**
 * <StudentAccountPage> — « Mon compte » de l'élève (page).
 *
 * Dans l'ordre de l'écran d'aujourd'hui, et seulement ce qu'un élève y voit : ce qu'il lui reste
 * à finir (valider son email, compléter son profil), qui il est, sa progression (titre, quêtes,
 * classements, gestes), ses statistiques, l'entrée vers ses paramètres, et « Se déconnecter » : le
 * bouton d'aujourd'hui dit « Changer de compte », mais il déconnecte, le même geste et le même
 * bouton que le pied de la feuille « moi » (Wajih, 16/10).
 * Pas d'abonnement, de parrainage, de bascule vers l'espace parent, d'apparence ni de suppression
 * ici : ils n'y sont pas aujourd'hui.
 *
 * Une ligne n'existe que si son geste existe, comme au portail d'abonnement. La gamification
 * tient en un bloc (`gamification`) : sans lui, ni titre, ni XP, ni pétales, ni les trois lignes.
 *
 * `titleProgress` pose le titre dans la carte d'identité, avec l'anneau du livre et le titre
 * suivant : la ligne « Titre » de la progression ne se rend alors plus, le même geste (choisir son
 * titre) ne s'offre pas deux fois.
 *
 * C'est d'ici que l'élève rejoint les écrans du jeu : « Quêtes » ouvre `<StudentQuestsPage>`,
 * « Classements » `<StudentRankingPage>`, « Gestes » `<StudentShopPage>`. L'éditeur d'avatar, lui,
 * reste celui de l'ancien code : son entrée est le crayon de l'avatar (« Personnaliser mon
 * avatar »). L'avatar par IA s'ouvre du même menu, dans sa feuille (`avatarAI`,
 * `<AccountAvatarAIModal>`) ; la validation de l'email est sa propre page
 * (`<StudentValidateEmailPage>`).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ProgressBar } from "../../atoms/ProgressBar"
import { columnStyles } from "../../atoms/containers/columns"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AccountSignOut } from "../../molecules/AccountSignOut"
import { ClosableHeader } from "../../molecules/ClosableHeader"
import { SettingsSection, type SettingsSectionRow } from "../../molecules/SettingsSection"
import { Snackbar, type SnackbarProps } from "../../molecules/Snackbar"
import {
    AccountIdentityCard,
    type AccountIdentityCardProps,
    type UIStudentAccountIdentity,
} from "../../organisms/AccountIdentityCard"
import { ActivityStatsCard, type UIActivityStatsPeriod } from "../../organisms/ActivityStatsCard"
import { AccountAvatarAIModal, type AccountAvatarAIModalProps } from "../../templates/AccountAvatarAIModal"
import { AccountAvatarMenu, type AccountAvatarMenuProps } from "../../templates/AccountAvatarMenu"
import { AccountTitleModal, type AccountTitleModalProps } from "../../templates/AccountTitleModal"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { compact } from "../../utils/compact"
import { StudentAccountSkeleton } from "./StudentAccountSkeleton"
import { type UIStudentAccountGamification } from "../../../ports/primary/ui/types"

export type { UIStudentAccountGamification }

export type StudentAccountNotice = Pick<SnackbarProps, "message" | "tone">

export type StudentAccountPageProps = {
    /** `null` tant que le compte n'est pas là : le corps ne se rend pas. */
    identity: UIStudentAccountIdentity | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    /** La croix de l'en-tête. */
    onClose: () => void

    /** « Valide ton adresse email » : présent tant que l'email n'est pas validé. */
    onValidateEmail?: () => void
    /** « Complète ton profil » : présent tant que le profil n'est pas complet. */
    onCompleteProfile?: () => void
    /** L'avancement du profil, de 0 à 100. */
    profileCompletion?: number | null

    /** Absent : l'écran sans gamification. */
    gamification?: UIStudentAccountGamification | null
    /**
     * Le titre de l'élève et son chemin vers le suivant, dans la carte d'identité. Absent
     * (défaut) : le titre reste une ligne de la progression.
     */
    titleProgress?: AccountIdentityCardProps["titleProgress"]
    onChooseTitle?: () => void
    onOpenQuests?: () => void
    onOpenRanking?: () => void
    onOpenShop?: () => void

    /** « Mes statistiques » : absentes tant que le serveur n'en donne pas. */
    statistics?: UIActivityStatsPeriod[] | null
    initialStatisticsPeriodKey?: string

    onOpenSettings: () => void
    onSwitchAccount: () => void
    /** La sortie est en cours : le bouton tourne et ne répond plus. */
    switchingAccount?: boolean
    /** « 3.9.2 » */
    version?: string

    onEditAvatar?: () => void
    avatarBusy?: boolean
    /** Le menu de l'avatar et ses deux boîtes. */
    avatarMenu?: AccountAvatarMenuProps | null
    /** La feuille « Génère ton avatar IA ». Absente : pas de feuille. */
    avatarAI?: AccountAvatarAIModalProps | null
    /** La feuille du choix du titre. */
    titleModal?: AccountTitleModalProps | null
    /** L'issue d'un geste : un geste qui n'est pas passé (avatar, titre), une génération. */
    notice?: StudentAccountNotice | null
    onDismissNotice?: () => void
    testID?: string
}

export function StudentAccountPage(props: StudentAccountPageProps) {
    const {
        identity,
        loading = false,
        error,
        onRetry,
        onClose,
        onValidateEmail,
        onCompleteProfile,
        profileCompletion,
        gamification,
        titleProgress,
        onChooseTitle,
        onOpenQuests,
        onOpenRanking,
        onOpenShop,
        statistics,
        initialStatisticsPeriodKey,
        onOpenSettings,
        onSwitchAccount,
        switchingAccount = false,
        version,
        onEditAvatar,
        avatarBusy = false,
        avatarMenu,
        avatarAI,
        titleModal,
        notice,
        onDismissNotice,
        testID,
    } = props

    const todoRows = compact<SettingsSectionRow>([
        onValidateEmail && {
            key: "email",
            iconName: "envelope",
            label: "Valide ton adresse email",
            onPress: onValidateEmail,
        },
        onCompleteProfile && {
            key: "profile",
            iconName: "user",
            label: "Complète ton profil",
            description: "Pour que ton masteur puisse mieux te connaître",
            value: profileCompletion != null ? `${Math.round(profileCompletion)} %` : undefined,
            trailing:
                profileCompletion != null ? (
                    <ProgressBar value={profileCompletion} color="role" height={6} style={styles.todoBar} />
                ) : undefined,
            onPress: onCompleteProfile,
        },
    ])

    // Le titre vit dans la carte d'identité quand son anneau y est : pas deux fois le même geste.
    const titleInCard = gamification != null && titleProgress != null

    const progressRows =
        gamification == null
            ? []
            : compact<SettingsSectionRow>([
                  !titleInCard &&
                      onChooseTitle && {
                          key: "title",
                          iconName: "star",
                          label: "Titre",
                          value: gamification.title ?? undefined,
                          onPress: onChooseTitle,
                      },
                  onOpenQuests && {
                      key: "quests",
                      iconName: "flag",
                      label: "Quêtes",
                      trailing:
                          gamification.quest != null ? (
                              <Stack gap="1" style={styles.quest}>
                                  <Text size="text-sm" color="text-muted" numberOfLines={1}>
                                      {gamification.quest.label}
                                  </Text>
                                  <ProgressBar value={gamification.quest.progress} color="role" height={6} />
                              </Stack>
                          ) : undefined,
                      onPress: onOpenQuests,
                  },
                  onOpenRanking && {
                      key: "ranking",
                      iconName: "signal",
                      label: "Classements",
                      value: gamification.rankLabel ?? undefined,
                      onPress: onOpenRanking,
                  },
                  onOpenShop && {
                      key: "shop",
                      iconName: "gift",
                      label: "Gestes",
                      value: gamification.gesturesLabel ?? undefined,
                      onPress: onOpenShop,
                  },
              ])

    const header = <ClosableHeader title="Mon compte" onClose={onClose} />

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<StudentAccountSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            {identity != null && (
                <View style={[columnStyles.columns, styles.fullWidth]}>
                    <Stack gap="4" style={columnStyles.column}>
                        <SettingsSection title="À faire" rows={todoRows} />
                        <AccountIdentityCard
                            identity={identity}
                            xp={gamification?.xp}
                            petals={gamification?.petals}
                            onEditAvatar={onEditAvatar}
                            avatarBusy={avatarBusy}
                            titleProgress={titleInCard ? titleProgress : undefined}
                            onChooseTitle={titleInCard ? onChooseTitle : undefined}
                        />
                        <SettingsSection rows={progressRows} />
                    </Stack>

                    <Stack gap="4" style={columnStyles.column}>
                        {statistics != null && statistics.length > 0 && (
                            <ActivityStatsCard periods={statistics} initialPeriodKey={initialStatisticsPeriodKey} />
                        )}
                        <SettingsSection
                            rows={[{ key: "settings", iconName: "cog", label: "Paramètres", onPress: onOpenSettings }]}
                        />
                        <AccountSignOut onSignOut={onSwitchAccount} signingOut={switchingAccount} version={version} />
                    </Stack>
                </View>
            )}

            {avatarMenu != null && <AccountAvatarMenu {...avatarMenu} />}
            {avatarAI != null && <AccountAvatarAIModal {...avatarAI} />}
            {titleModal != null && <AccountTitleModal {...titleModal} />}
            <Snackbar
                visible={notice != null}
                message={notice?.message ?? ""}
                tone={notice?.tone ?? "danger"}
                onDismiss={onDismissNotice ?? NOOP}
            />
        </PageTemplate>
    )
}

/** Sans `onDismissNotice`, rien à fermer. */
const NOOP = () => {}

const styles = StyleSheet.create(() => ({
    /** La largeur de la colonne appartient à la page (`PageTemplate maxContentWidth`). */
    fullWidth: { width: "100%" },
    todoBar: { width: 48 },
    quest: { width: 128 },
}))
